import Utils from '../../../scripts/vendors/utils';

/**
 * Jobs
 * @class
 * @constructor
 * @public
 */
class Jobs {
  /**
   * Constructor
   * @param {string} name The container to handle
   * @returns {object} options The options
   * @returns {class}
   */
  constructor() {
    this.component = document.querySelector('[data-jobs]');

    // Settings
    this.settings = {
      endpoint: {
        cache: '/assets/cf-jobs.json',
        live: 'https://careforce24.de/api/jobs/json/set/website/de'
      }
    };

    // Engage engines
    if (this.component) {
      this.settings.endpoint = this.component.getAttribute('data-jobs-source') || this.settings.endpoint.cache;
      this.i18n = JSON.parse(this.component.getAttribute('data-jobs-i18n'));

      // Filter params
      this.filters = {
        region: [],
        subCategory: [],
        zipCode: '',
        indication: [],
      };

      // Pager params
      this.pager = {
        resultsCount: 0,
        currentPage: 0,
        currentPagesCount: 0,
        itemsPerPage: 10
      }

      // Other vars
      this.countFilters = 0;
      this.hasChangeActive = true;
      this.allRegions = [];
      this.allSubCategories = [];
      this.allIndications = [];
      this.currentJobCategory = this.component.getAttribute('data-jobs-category') || false;

      // Elements
      this.resetFilterButton = this.component.querySelector('[data-jobs-filter-reset]')
      this.countFilterContainer = this.resetFilterButton.querySelector('span:last-child')
      this.filterRegionContainer = this.component.querySelector('[data-jobs-filter-region]');
      this.filterZipcodeContainer = this.component.querySelector('[data-jobs-filter-zipcode]');
      this.filterSubCategoryContainer = this.component.querySelector('[data-jobs-filter-subcategory]');
      this.filterIndicationContainer = this.component.querySelector('[data-jobs-filter-indication]');
      this.listingContainer = this.component.querySelector('[data-jobs-listing]');
      this.paginationContainer = this.component.querySelector('.pagination')
      this.noResultsMessage = this.component.querySelector('[data-jobs-noresults]');
      this.loadingIndicator = this.component.querySelector('[data-jobs-loading]');

      this.init();
    }
  }

  /**
   * Initialize the component
   * @returns {void}
   */
  init() {
    fetch(this.settings.endpoint)
      .then(response => response.json())
      .then(json => {
        if (json && json.hasOwnProperty('recordset')) {
          this.data = json.recordset;
          if (this.loadingIndicator) {
            this.loadingIndicator.parentNode.removeChild(this.loadingIndicator);
          }

          // run component functions
          this.parseData();
          this.buildFilters();
          this.readPopState();
          this.buildResults();
          this.buildPager();
          this.applyFilters();

          this.component.classList.add('is-initialized');

          // enable reset button
          this.resetFilterButton.addEventListener('click', this.resetFilters.bind(this));

          // enable pagination buttons
          this.paginationContainer.querySelectorAll('.pagination__link').forEach(link => {
            link.addEventListener('click', (e) => {
              e.preventDefault();
              if (e.target.classList.contains('pagination__link--prev') && this.pager.currentPage > 0) {
                this.showPage(this.pager.currentPage - 1);
              } else if (e.target.classList.contains('pagination__link--next') && this.pager.currentPage + 1 < this.pager.currentPagesCount) {
                this.showPage(this.pager.currentPage + 1);
              }
              this.scrollToComponent();
              this.buildPager();
              this.setPopState();
            });
          });

          // listen to hashchange
          window.addEventListener('hashchange', this.readPopState.bind(this));
        }
      });
  }

  /**
   * Parse and sort data
   * @returns {void}
   */
  parseData() {
    this.data.sort(function(a, b){
      return new Date(Utils.string.removeCdata(b.jobPublishingDateFrom)) - new Date(Utils.string.removeCdata(a.jobPublishingDateFrom));
    });

    for (let job of this.data) {

      if (!this.currentJobCategory || this.currentJobCategory === 'Alle' || (job.hasOwnProperty('jobCategory_1') && this.currentJobCategory === job.jobCategory_1) || (job.hasOwnProperty('jobCategory_2') && this.currentJobCategory === job.jobCategory_2)) {
        // fill filter arrays with job.jobRegion, job.jobSubcategory and job.jobIndication, add allRegions to job object
        if (job.hasOwnProperty('Bundesland') && job.Bundesland !== null) {
          const allRegions = Utils.string.removeCdata(job.Bundesland).split(', ');
          allRegions.forEach(region => {
            if (!this.allRegions.includes(region)) {
              this.allRegions.push(region);
            }
          });
          job.allRegions = allRegions.map(region => region);
        }

        if (job.hasOwnProperty('jobSubcategory') && (job.jobSubcategory != null && !this.allSubCategories.includes(job.jobSubcategory) && job.jobSubcategory != '' && this.filterSubCategoryContainer)) {
          this.allSubCategories.push(job.jobSubcategory);
        }

        // Build Indications array
        if (job.hasOwnProperty('jobIndications') && job.jobIndications !== null ) {
            let indications = job.jobIndications;
            for (let i = 0; i < indications.length; i++) {
                if (!this.allIndications.includes(indications[i])) {
                    this.allIndications.push(indications[i]);
                }
            }
        }

        // remove last character from job.projectFurtherPlz and add zipCodes to job object
        if (job.hasOwnProperty('projectFurtherPlz') && job.projectFurtherPlz !== null) {
          const jobZipCodes = Utils.string.removeCdata(job.projectFurtherPlz).split(',');
          job.zipCodes = jobZipCodes.map(zip => zip.substring(0, zip.length - 1));
        }
      }
    }



    this.allRegions.sort();
    this.allSubCategories.sort();
    this.allIndications.sort();
  }

  /**
   * Build filters
   * @returns {void}
   */
  buildFilters() {
    // checkboxes for the list of regions
    if (this.allRegions.length && this.filterRegionContainer) {
      for (const i in this.allRegions) {
        const checkbox = `<label class="control control--checkbox" for="region-${i}">
                <span>${this.allRegions[i]}</span>
                <input type="checkbox" name="region" value="${this.allRegions[i]}" id="region-${i}" />
                <div class="control__indicator"></div>
            </label>`;
        this.filterRegionContainer.insertAdjacentHTML('beforeend', checkbox);
      }
    }

    // checkboxes for the list of subcategories
    if (this.allSubCategories.length && this.filterSubCategoryContainer) {
      for (const i in this.allSubCategories) {
        const checkbox = `<label class="control control--checkbox" for="subCategory-${i}">
                <span>${this.allSubCategories[i]}</span>
                <input type="checkbox" name="subCategory" value="${this.allSubCategories[i]}" id="subCategory-${i}" />
                <div class="control__indicator"></div>
            </label>`;
        this.filterSubCategoryContainer.insertAdjacentHTML('beforeend', checkbox);
        console.log(this.allSubCategories[i]|length, this.allSubCategories[i], typeof this.allSubCategories[i] === 'string', this.allSubCategories[i] === null);
      }
    }

    // select options for the list of indications
    if (this.allIndications.length && this.filterIndicationContainer) {
      for (const i in this.allIndications) {
        const checkbox = `<label class="control control--checkbox" for="indication-${i}">
                <span>${this.allIndications[i]}</span>
                <input type="checkbox" name="indication" value="${this.allIndications[i]}" id="indication-${i}" />
                <div class="control__indicator"></div>
            </label>`;
        this.filterIndicationContainer.insertAdjacentHTML('beforeend', checkbox);
      }
    }
  }

  /**
   * Build results list
   * @returns {void}
   */
  buildResults() {
    this.listingContainer.textContent = '';
    this.pager.resultsCount = 0;

    for (let job of this.data) {
      if (
        ( !this.currentJobCategory || this.currentJobCategory === 'Alle' || (job.hasOwnProperty('jobCategory_1') && this.currentJobCategory === job.jobCategory_1) || (job.hasOwnProperty('jobCategory_2') && this.currentJobCategory === job.jobCategory_2) ) && // is currentJobCategory
        ( this.filters.region.length === 0 || (job.hasOwnProperty('Bundesland') && this.filters.region.filter(region => job.allRegions.includes(region)).length) ) && // is in filters.region
        ( this.filters.subCategory.length === 0 || (job.hasOwnProperty('jobSubcategory') && this.filters.subCategory.indexOf(job.jobSubcategory) > -1) ) && // is in filters.subCategory
        ( this.filters.zipCode === '' || (job.hasOwnProperty('projectFurtherPlz') && job.zipCodes.indexOf(this.filters.zipCode) > -1) ) && // is in filters.zipCode
        ( this.filters.indication.length === 0 || (job.hasOwnProperty('jobIndications') && this.filters.indication.some(r=> job.jobIndications.indexOf(r) >= 0)) // is in filters.indication
      )) {
        this.pager.resultsCount++;

        const item = `<li class="job__listing__item">
            <h4 class="headline headline--4">${Utils.string.removeCdata(job.Titel)}</h4>
            <ul class="jobs__listing__properties">
                <li class="jobs__listing__properties--location">${this.i18n.area}: ${Utils.string.removeCdata(job.projectGebiet)}</li>` +
                (job.jobIndications ? `<li class="jobs__listing__properties--indication">${this.i18n.indication}: ${job.jobIndications}</li>` : '') +
          `</ul>
            <p>${Utils.string.removeCdata(job.jobSummary)}</p>
            <a href="${Utils.string.removeCdata(job.URL)}" class="button button--secondary" target="_blank" rel="noopener">${this.i18n.showDetails}</a>
        </li>`;
        this.listingContainer.insertAdjacentHTML('beforeend', item);
      }
    }

    if (this.noResultsMessage) {
      this.noResultsMessage.classList.toggle('hidden', this.pager.resultsCount > 0);
    }
  }

  /**
   * Apply selected filters
   * @returns {void}
   */
  applyFilters() {
    const filters = this.component.querySelectorAll('input, select');
    filters.forEach(filter => {
      filter.addEventListener('change', () => {
        const filterName = filter.getAttribute('name');
        const filterValue = filter.value;

        // update filters arrays
        if (filterName === 'region' || filterName === 'subCategory' || filterName === 'indication') {
          if (filter.checked) {
            this.countFilters += 1;
            this.filters[filterName].push(filterValue);
          } else {
            this.countFilters -= 1;
            this.filters[filterName] = this.filters[filterName].filter(item => item !== filterValue);
          }
        } else if (filterName === 'zipCode') {
          if (this.filters[filterName] === '' && filterValue !== '') {
            this.countFilters += 1;
          } else if (filterValue === '') {
            this.countFilters -= 1;
          }
          this.filters[filterName] = filterValue;
        }

        // refresh results
        this.pager.currentPage = 0;
        this.updateFilterCount();
        this.buildResults();
        this.buildPager();
        this.setPopState();
      });
    });
  }

  /**
   * Reset all filters
   * @returns {void}
   */
  resetFilters() {
    const filters = this.component.querySelectorAll('input, select');

    filters.forEach(filter => {
      if (filter.tagName.toLowerCase() === 'input') {
        filter.checked = false;
      } else if (filter.tagName.toLowerCase() === 'select') {
        filter.selectedIndex = 0;
      }
    });

    this.filters = {
      region: [],
      subCategory: [],
      zipCode: '',
      indication: [],
    };

    this.pager.currentPage = 0;
    this.countFilters = 0;

    this.updateFilterCount();
    this.buildResults();
    this.buildPager();
    this.setPopState();
  }

  /**
   * Show filter count
   * @returns {void}
   */
  updateFilterCount() {
    this.countFilterContainer.textContent = this.countFilters;
    this.countFilterContainer.classList.toggle('is-visible', this.countFilters > 0);
    this.resetFilterButton.classList.toggle('is-visible', this.countFilters > 0);
  }

  /**
   * Build pager
   * @returns {void}
   */
  buildPager() {
    const list = this.paginationContainer.querySelector('.pagination__list');
    list.textContent = '';

    this.pager.currentPagesCount = Math.ceil(this.pager.resultsCount / this.pager.itemsPerPage);

    if (this.pager.currentPagesCount > 1) {
      for (let index = 0; index < this.pager.currentPagesCount; index++) {

        if (
          index === 0 || // first page
          index === this.pager.currentPagesCount - 1 || // last page
          index === this.pager.currentPage || // current page
          index === this.pager.currentPage - 1 || // previous page
          index === this.pager.currentPage + 1 || // next page
          (index < 3  && this.pager.currentPage === 0) || // additional next page for first page
          (index + 3 === this.pager.currentPagesCount && this.pager.currentPage + 1 === this.pager.currentPagesCount)) // additional previous page for last page
        {

          // append dots before last button
          if (index + 1 === this.pager.currentPagesCount && this.pager.currentPage < this.pager.currentPagesCount - 3) {
            const dots = document.createElement('li');
            dots.textContent = '...';
            list.appendChild(dots);
          }

          // create pagination button
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.classList.add('button', index === this.pager.currentPage ? 'button--highlight' : 'button--secondary');
          link.textContent = (index + 1).toString();
          item.appendChild(link);
          list.appendChild(item);

          // append dots after first button
          if (index === 0 && this.pager.currentPage >= 3) {
            const dots = document.createElement('li');
            dots.textContent = '...';
            list.appendChild(dots);
          }

          // add click events
          item.addEventListener('click', () => {
            this.showPage(index);
            this.scrollToComponent();
            this.buildPager();
            this.setPopState();
          });
        }

      }

      this.showPage(this.pager.currentPage);
    }

    this.paginationContainer.classList.toggle('is-visible', this.pager.currentPagesCount > 1);
  }

  /**
   * Apply paging
   * @returns {void}
   */
  showPage(page) {
    const jobs = this.listingContainer.querySelectorAll('.job__listing__item');
    this.pager.currentPage = page;

    const start = this.pager.currentPage * this.pager.itemsPerPage;
    const end = start + this.pager.itemsPerPage;

    jobs.forEach((job, index) => {
      const isVisible = index >= start && index < end;
      job.classList.toggle('hidden', !isVisible);
    });
  }

  /**
   * Set popState
   * @returns {void}
   */
  setPopState() {
    const hash = new URLSearchParams(this.filters);
    hash.set('page', this.pager.currentPage);

    this.hasChangeActive = false;
    window.location.hash = hash.toString();

    setTimeout(() => {
      this.hasChangeActive = true;
    }, 50);
  }

  /**
   * Read popState
   * @returns {void}
   */
  readPopState() {
    if (this.hasChangeActive) {
      const hash = window.location.hash.substring(1);
      const urlParams = new URLSearchParams(hash);
      let countFilters = 0;

      this.filters = {
        region: hash.length && urlParams.get('region') ? urlParams.get('region').split(',') : [],
        subCategory: hash.length && urlParams.get('subCategory') ? urlParams.get('subCategory').split(',') : [],
        zipCode: hash.length ? urlParams.get('zipCode') : '',
        indication: hash.length && urlParams.get('indication') ? urlParams.get('indication').split(',') : [],
      };

      this.pager.currentPage = hash.length && urlParams.get('page') ? parseInt(urlParams.get('page')) : 0;

      if (this.filterRegionContainer) {
        this.filterRegionContainer.querySelectorAll('input[name="region"]').forEach(input => {
          if (this.filters.region.includes(input.value)) {
            input.checked = true;
            countFilters += 1;
          } else {
            input.checked = false;
          }
        });
      }

      if (this.filterSubCategoryContainer) {
        this.filterSubCategoryContainer.querySelectorAll('input[name="subCategory"]').forEach(input => {
          if (this.filters.subCategory.includes(input.value)) {
            input.checked = true;
            countFilters += 1;
          } else {
            input.checked = false;
          }
        });
      }

      if (this.filterZipcodeContainer) {
        const option = this.filterZipcodeContainer.querySelector(`option[value="${this.filters.zipCode}"]`);
        if (option) {
          option.selected = true;
          if (this.filters.zipCode !== '') {
            countFilters += 1;
          }
        }
      }

      if (this.filterIndicationContainer) {
        this.filterIndicationContainer.querySelectorAll('input[name="indication"]').forEach(input => {
          if (this.filters.indication.includes(input.value)) {
            input.checked = true;
            countFilters += 1;
          } else {
            input.checked = false;
          }
        });
      }

      this.countFilters = countFilters;
      this.updateFilterCount();
      this.buildResults();
      this.buildPager();

    }
  }

  /**
   * Scroll to component
   * @returns {void}
   */
  scrollToComponent() {
    setTimeout(() => {
      window.scrollTo({
        top: window.scrollY + this.component.getBoundingClientRect().top,
        behavior: 'smooth'
      });
    }, 200);
  }

}

module.exports = Jobs;
